import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import { appFetchJson } from "../../../utils/http";
import apiRoutes from "../../../api/apiRoutes";
import EessAutocomplete from "../../../components/autocomplete/EessAutocomplete";
import ProductoAutocomplete from "../../../components/autocomplete/ProductoAutocomplete";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";

export default function EditarPrecioProductoEessFuturo({
  onClose = () => {},
  onUpdated = () => {},
  record = null,
}) {
  
  // --- AYUDANTE: FECHA DE MAÑANA (YYYY-MM-DD) ---
  const getTomorrowDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // --- ESTADOS ---
  const [fechaVigencia, setFechaVigencia] = useState("");
  const [selectedEess, setSelectedEess] = useState(null);
  const [selectedProducto, setSelectedProducto] = useState(null);
  
  const [valorPrecio, setValorPrecio] = useState("");
  const [precioCompra, setPrecioCompra] = useState("");
  
  // false = Pendiente (99), true = Activo (98)
  const [activar, setActivar] = useState(false);
  
  // ESTADO DE BLOQUEO: Controla si el formulario es de solo lectura
  const [isBloqueado, setIsBloqueado] = useState(false);

  const [saving, setSaving] = useState(false);

  const isValid = selectedEess !== null && selectedProducto !== null && valorPrecio !== "" && fechaVigencia !== "";

  // --- CARGAR DATOS AL ABRIR ---
  useEffect(() => {
    if (record) {
      // 1. FECHA
      if (record.fechaVigenciaIni) {
        const fechaLimpia = record.fechaVigenciaIni.toString().split("T")[0];
        setFechaVigencia(fechaLimpia);
      } else {
        setFechaVigencia(getTomorrowDate());
      }

      // 2. Precios
      setValorPrecio(record.precio || 0);
      setPrecioCompra(record.precio2 || 0);

      // 3. ESTADO (LÓGICA CORREGIDA)
      const idEstado = Number(record.estadoId || record.estado);
      const descEstado = (record.estadoDescripcion || "").toUpperCase();

      const esActivo = 
          idEstado === 98 || 
          idEstado === 12 || 
          descEstado === "PRECIO PRODUCTO ACTIVO";

      if (esActivo) {
        setActivar(true);
        // SI ES ACTIVO, BLOQUEAMOS LA EDICIÓN
        setIsBloqueado(true);
      } else {
        setActivar(false);
        // SI ES PENDIENTE (u otro), PERMITIMOS EDICIÓN
        setIsBloqueado(false);
      }

      // 4. Estación
      if (record.eessId) {
        setSelectedEess({
          id: record.eessId,
          label: record.eessNombre || "Estación",
          nombre: record.eessNombre
        });
      }

      // 5. Producto
      if (record.productoId) {
        setSelectedProducto({
          id: record.productoId,
          codigo: record.productoNombre,
          nombre: record.productoNombre
        });
      }
    }
  }, [record]);

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const guardar = async () => {
    if (!isValid || saving) return;
    setSaving(true);

    try {
      const payload = {
        id: record.id,
        eessId: selectedEess?.id,
        productoId: selectedProducto?.id,
        fechaVigenciaIni: fechaVigencia,
        precio: Number(valorPrecio),
        precio2: Number(precioCompra),
        estadoId: activar ? 98 : 99
      };

      await appFetchJson(apiRoutes.precioProductoEessFuturo.update, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (onUpdated) onUpdated();
      onClose();

    } catch (e) {
      console.error("Error al editar:", e);
      alert(`Error al guardar cambios: ${e.message || "Error 500"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">
            {isBloqueado ? "Precio Futuro (Activo)" : "Editar Precio Futuro"}
          </h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body flex flex-col items-start">
          <p className={`mb-6 italic text-left w-full px-8 ${isBloqueado ? "text-red-600 font-bold" : "text-gray-600"}`}>
  {isBloqueado 
    ? "Este registro ya se encuentra ACTIVO y no puede ser modificado."
    : "Modifique la programación del precio futuro."}
</p>

          <div className="flex flex-col gap-3 mb-6 w-full px-8">
            {/* EESS */}
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">EE.SS.:</label>
              {/* Bloqueamos clicks en el wrapper si está bloqueado */}
              <div className={`w-[600px] ${isBloqueado ? "pointer-events-none opacity-50 bg-gray-100 rounded" : ""}`}>
                <EessAutocomplete 
                  value={selectedEess} 
                  onChange={setSelectedEess} 
                  placeholder="Buscar Estación..."
                />
              </div>
            </div>

            {/* FECHA */}
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold text-blue-700">Inicio Vigencia:</label>
              <div className="w-[600px]">
                <input
                  type="date"
                  className={`input w-full h-[38px] border rounded px-3 
                    ${isBloqueado ? 'bg-gray-200 border-gray-300' : 'bg-white border-blue-300 focus:border-blue-500'}`}
                  value={fechaVigencia}
                  min={getTomorrowDate()}
                  onChange={(e) => setFechaVigencia(e.target.value)}
                  disabled={isBloqueado}
                />
                {!isBloqueado && (
                    <p className="text-xs text-gray-500 mt-1 text-left">
                    Modifique la fecha en que se aplicará este precio.
                    </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex mb-0 w-full">
            <button className={`px-6 py-2 rounded-t-md font-medium text-sm text-white
                ${isBloqueado ? 'bg-gray-500' : 'bg-green-700'}`}>
              Valores del Producto
            </button>
          </div>

          <div className="bg-gray-100 p-8 rounded-b-md rounded-tr-md space-y-5 w-full">
            {/* Producto */}
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">Producto:</label>
              {/* Bloqueamos clicks en el wrapper si está bloqueado */}
              <div className={`w-[600px] ${isBloqueado ? "pointer-events-none opacity-50 bg-gray-100 rounded" : ""}`}>
                <ProductoAutocomplete 
                  value={selectedProducto} 
                  onChange={setSelectedProducto}
                />
              </div>
            </div>

            {/* Precios */}
            <div className="flex items-center mt-6">
              <label className="w-32 shrink-0 text-left font-bold">Valor Precio:</label>
              <div className="w-[600px]">
                <InputNumberNoScroll
                  className={`input w-full h-[38px] border rounded px-3 
                    ${isBloqueado ? 'bg-gray-200' : 'bg-white'}`}
                  value={valorPrecio}
                  onChange={(e) => setValorPrecio(e.target.value)}
                  disabled={isBloqueado}
                />
              </div>
            </div>

            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">Precio Compra:</label>
              <div className="w-[600px]">
                <InputNumberNoScroll
                  className={`input w-full h-[38px] border rounded px-3
                    ${isBloqueado ? 'bg-gray-200' : 'bg-white'}`}
                  value={precioCompra}
                  onChange={(e) => setPrecioCompra(e.target.value)}
                  disabled={isBloqueado}
                />
              </div>
            </div>

          </div>
        </div>

        <div className="modal-footer">
          <div className="btn-row">
            {/* Solo mostramos el botón GUARDAR si NO está bloqueado */}
            {!isBloqueado && (
                <BtnGuardar label="Guardar Cambios" isValid={isValid} saving={saving} onClick={guardar} />
            )}
            <BtnCancelar onClick={onClose} label={isBloqueado ? "Cerrar" : "Cancelar"} />
          </div>
        </div>
      </div>
    </div>
  );
}